/** How long an in-app join waits for `/api/auth/me` before publishing the URL identity. */
export const IN_APP_IDENTITY_MS = 2500;

export function mergeAuthIdentity(me, fallback) {
  const base = fallback || {};
  const userId = me?.userId || me?.userid || base.userId || base.userid || "";
  const named = me?.name && me.name !== userId ? me.name : "";
  const prevNamed = base.name && base.name !== userId ? base.name : "";
  return {
    userId,
    userid: userId,
    name: named || prevNamed || userId || "Guest",
    avatarUrl: me?.avatarUrl || me?.avatar || base.avatarUrl || "",
    punkId: me?.punkId ?? base.punkId ?? null,
    address: me?.address || base.address || "",
    via: me?.via || base.via || "wallet",
  };
}

/** Attributes already on a published stream, with a newer profile written over them. */
export function publishedAttributes(identity, current = {}) {
  const name = identity?.name || current.name || "";
  const userId = identity?.userId || identity?.userid || current.userid || current.userId || "";
  return {
    ...current,
    name,
    actualName: name,
    nickname: name,
    userid: userId,
    id: userId,
    avatar: identity?.avatarUrl || "",
    avatarUrl: identity?.avatarUrl || "",
    punkId: identity?.punkId ?? null,
    address: identity?.address || "",
  };
}

/**
 * Call `onIdentity` once the profile is known, or with `fallback` when `timeoutMs` passes.
 * If the profile arrives after that, call `onIdentity` again with phase `"late"`.
 * Returns a stop function.
 */
export function followAuthIdentity(fetchMe, fallback, { timeoutMs = IN_APP_IDENTITY_MS, onIdentity } = {}) {
  let opened = false;
  let stopped = false;
  const give = (identity, phase) => {
    if (stopped) return;
    onIdentity?.(identity, phase);
  };
  const timer = setTimeout(() => {
    if (opened || stopped) return;
    opened = true;
    give(fallback, "timeout");
  }, timeoutMs);
  Promise.resolve()
    .then(() => fetchMe())
    .then((me) => {
      clearTimeout(timer);
      if (stopped) return;
      const next = me?.authenticated ? mergeAuthIdentity(me, fallback) : fallback;
      if (!opened) {
        opened = true;
        give(next, "ready");
      } else if (me?.authenticated) {
        give(next, "late");
      }
    })
    .catch(() => {
      clearTimeout(timer);
      if (stopped || opened) return;
      opened = true;
      give(fallback, "error");
    });
  return () => {
    stopped = true;
    clearTimeout(timer);
  };
}
